/**
 * 文件定位：
 * 1. 当前文件位于 Worker 倒角求解 class 层；
 * 2. 负责 SolidWorks Face Face 倒角的 Puppy CAD 内核 构建；
 * 3. 面组、弦宽度换算和帮助点解析委托给纯函数层。
 *
 * 文件职责：
 * 1. 校验 Face Face 倒角方法参数；
 * 2. 解析两组面之间的共享边；
 * 3. 根据 symmetric / asymmetric / chord-width 调用对应 Puppy CAD 内核 注册方式。
 */

import {
  assertChamferBuilderResultSafe,
  buildProgressRange,
  safeDelete,
} from '../functions/chamferPuppyKernel';
import {
  collectResolvedFaceFaceSharedEdges,
  filterResolvedFaceFaceSharedEdgesByHelpPoint,
  resolveDistanceFromFaceFaceChamferChordWidthOnFaces,
  resolveFaceFaceChamferMode,
} from '../functions/faceFaceChamferGeometry';
import type { ChamferShapeBuildInput } from '../types/ChamferShapeBuildInput';
import { ChamferSolver } from './ChamferSolver';

/**
 * Face Face 倒角求解器。
 */
export class FaceFaceChamferSolver extends ChamferSolver {
  
  public readonly type = 'face-face' as const;

  /**
   * @inheritdoc
   */
  public build(input: ChamferShapeBuildInput): unknown {
    const { puppy, sourceShape, sourceMetadata, definition } = input;
    const mode = resolveFaceFaceChamferMode(definition);
    const usesChordWidth = mode === 'chord-width';
    const sharedEdges = collectResolvedFaceFaceSharedEdges(
      puppy,
      sourceShape,
      sourceMetadata,
      input.sourceObjectId,
      definition,
    );
    const filteredSharedEdges = filterResolvedFaceFaceSharedEdgesByHelpPoint(
      sharedEdges,
      definition.helpPoint ?? null,
    );

    if (filteredSharedEdges.length === 0) {
      throw new Error('Face Face Chamfer 未找到两组面之间的共享边');
    }

    this.assertParameters(input, usesChordWidth, mode);

    const builder = new puppy.BRepFilletAPI_MakeChamfer(sourceShape);
    const progress = buildProgressRange(puppy);

    try {
      filteredSharedEdges.forEach((sharedEdge) => {
        const { edgeShape, leftFace, rightFace } = sharedEdge;
        if (usesChordWidth) {
          const effectiveDistance = resolveDistanceFromFaceFaceChamferChordWidthOnFaces(
            puppy,
            definition.width as number,
            leftFace,
            rightFace,
          );
          builder.Add_2(effectiveDistance, edgeShape);
          return;
        }
        if (mode === 'asymmetric') {
          /**
           * SolidWorks 的“反转方向”本质上交换距离 1/距离 2 所属的支撑面。
           * 方向必须在 worker 内按已解析的面组处理，UI 不参与拓扑顺序判断。
           */
          const supportFace = definition.flipDirection === true ? rightFace : leftFace;
          builder.Add_3(
            definition.distance as number,
            definition.distance2 as number,
            edgeShape,
            supportFace,
          );
          return;
        }
        builder.Add_2(definition.distance as number, edgeShape);
      });

      builder.Build(progress);
      return assertChamferBuilderResultSafe(puppy, builder);
    } finally {
      safeDelete(progress);
      safeDelete(builder);
    }
  }

  /**
   * 校验 Face Face 倒角参数。
   *
   * @param input 倒角求解输入。
   * @param usesChordWidth 是否为弦宽模式。
   * @param mode 当前 Face Face 模式。
   */
  private assertParameters(
    input: ChamferShapeBuildInput,
    usesChordWidth: boolean,
    mode: NonNullable<ChamferShapeBuildInput['definition']['faceFaceMode']>,
  ): void {
    const { definition } = input;
    if (usesChordWidth) {
      if (typeof definition.width !== 'number' || !Number.isFinite(definition.width) || definition.width <= 0) {
        throw new Error('Face Face Chamfer 的弦宽度模式缺少有效宽度');
      }
    } else if (
      typeof definition.distance !== 'number'
      || !Number.isFinite(definition.distance)
      || definition.distance <= 0
    ) {
      throw new Error('Face Face Chamfer 缺少有效距离');
    }

    if (mode === 'asymmetric' && (
      typeof definition.distance2 !== 'number'
      || !Number.isFinite(definition.distance2)
      || definition.distance2 <= 0
    )) {
      throw new Error('Face Face Chamfer 的非对称模式缺少有效距离 2');
    }
  }
}
